import { ImageFieldProps, LinkProps } from '@/types/types'

import { MainColumn, MainColumnLeft, MainColumnRight } from '@/components/main-column/MainColumn'
import AppImage from '@/components/AppImage'
import Link from 'next/link'

export type LogoGridItemProps = {
  logo?: ImageFieldProps
  title: string
  text: string
  link?: string
}

export type LogoGridProps = {
  columns?: 3 | 4 | 5 | 'text'
  items: LogoGridItemProps[]
  heading?: string
  title?: string
  titleTag?: string
  titleClass?: string
  text?: string
  button?: LinkProps | null
}

export default function LogoGrid({
  columns = 4,
  items = [],
  heading,
  title,
  text,
  button,
}: LogoGridProps) {
  if (!items || !items.length) {
    return null
  }

  const componentClass = columns === 'text' ? 'simple-component-wide' : ''

  const hasContent = columns === 'text' && (title || text)

  let gridClass = ''

  if (columns == 5 && items.length >= 5) {
    gridClass = 'grid-cols-1 @[600px]/main:grid-cols-2 @[1240px]/main:grid-cols-5'
  } else if (columns == 4 && items.length >= 4) {
    gridClass = 'grid-cols-1 @[600px]/main:grid-cols-2 @[1240px]/main:grid-cols-4'
  } else {
    gridClass = 'grid-cols-1 @[600px]/main:grid-cols-2 @[1240px]/main:grid-cols-3'
  }

  return (
    <>
      <div className={`component component--logo-grid ${componentClass}`}>
        {hasContent ? (
          <MainColumn>
            <MainColumnLeft
              heading={heading}
              title={title}
              titleTag="h2"
              titleClass="h2"
              text={text}
              button={button}
            />

            <MainColumnRight>
              <div className="@container/main">
                <div className={`grid gap-m ${gridClass}`}>
                  {items.map((item, index) => {
                    const { logo, title, text, link } = item

                    return (
                      <LogoGridItem key={index} logo={logo} title={title} text={text} link={link} />
                    )
                  })}
                </div>
              </div>
            </MainColumnRight>
          </MainColumn>
        ) : (
          <div className="container @container/main">
            <div className={`grid gap-m ${gridClass}`}>
              {items.map((item, index) => {
                const { logo, title, text, link } = item

                return (
                  <LogoGridItem key={index} logo={logo} title={title} text={text} link={link} />
                )
              })}
            </div>
          </div>
        )}
      </div>
    </>
  )
}

const itemClass = `
  p-s
  @[300px]/item:flex-row
  @[300px]/item:p-m 
`

const iconClass = `
  aspect-square
  
  w-[90px]
  @[300px]/item:w-[70px]
  
  @[330px]/item:w-[90px]
  @[405px]/item:w-[104px]
`

const transforms = {
  width: '104',
  height: '104',
  dpr: '2',
  cropMode: 'pad_resize',
  focus: 'center',
}

export function LogoGridItem({ logo, title, text, link }: LogoGridItemProps) {
  if (!logo && !title && !text && !link) {
    return null
  }

  const image = logo?.url ? (
    <AppImage
      path={logo.url}
      alt={title}
      className={`flex-shrink-0 ${iconClass}`}
      transforms={transforms}
      width={transforms.width}
      height={transforms.height}
    />
  ) : null

  return (
    <div className="rounded-md bg-theme text-fg @container/item theme--base-0 theme--base-0:border">
      <div className={`flex flex-col ${text ? 'items-start' : 'items-center'} gap-m ${itemClass}`}>
        {image ? <>{link ? <Link href={link}>{image}</Link> : image}</> : null}

        <div className="flex flex-col gap-xxs">
          {link ? (
            <Link href={link} className="h5 @[320px]/item:h4 !mb-0 hover:text-link">
              {title}&nbsp;&gt;
            </Link>
          ) : (
            <span className="h5 @[320px]/item:h4 !mb-0">{title}</span>
          )}

          <div className="p--small style-lists" dangerouslySetInnerHTML={{ __html: text }} />
        </div>
      </div>
    </div>
  )
}
